import React, { lazy } from 'react';
import { EventsScheduleSection } from './EventsScheduleSection';

/**
 * Lazy-loaded page components for each section.
 * These are wrapped in React.lazy so the parent Suspense boundary (in AttendeeMenuShell)
 * provides a loading fallback while the chunk is fetched.
 *
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 3.7, 3.8, 3.9
 */
const LazyMyWeekendPage = lazy(() =>
  import('../../pages/MyWeekendPage').then((m) => ({ default: m.MyWeekendPage }))
);
const LazyGuestToolkitPage = lazy(() =>
  import('../../pages/GuestToolkitPage').then((m) => ({ default: m.GuestToolkitPage }))
);
const LazyMarketplacePage = lazy(() =>
  import('../../pages/MarketplacePage').then((m) => ({ default: m.MarketplacePage }))
);
const LazyAnnouncementsPage = lazy(() =>
  import('../../pages/AnnouncementsPage').then((m) => ({ default: m.AnnouncementsPage }))
);
const LazyChaptersPage = lazy(() =>
  import('../../pages/ChaptersPage').then((m) => ({ default: m.ChaptersPage }))
);
const LazyKrioFunFactsPage = lazy(() =>
  import('../../pages/KrioWisdomBrowsePage').then((m) => ({ default: m.KrioWisdomBrowsePage }))
);
const LazyKnowledgePage = lazy(() =>
  import('../../pages/KnowledgePage').then((m) => ({ default: m.KnowledgePage }))
);
const LazyStoryBuilderMyStoryPage = lazy(() =>
  import('../../pages/StoryBuilderMyStoryPage').then((m) => ({ default: m.StoryBuilderMyStoryPage }))
);
const LazySettingsPage = lazy(() =>
  import('../../pages/SettingsPage').then((m) => ({ default: m.SettingsPage }))
);

export interface SectionContentProps {
  currentIndex: number;
}

/**
 * SectionContent — Maps the current section index to the appropriate content component.
 * Each section renders the existing page component for that area. The Events & Schedule
 * section (index 2) renders the mobile-specific EventsScheduleSection with card layout.
 *
 * The parent AttendeeMenuShell wraps this in a Suspense boundary and keys the content
 * wrapper by currentIndex, forcing unmount/remount on section change (Requirement 3.10).
 *
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 3.7, 3.8, 3.9, 3.10
 */
export const SectionContent: React.FC<SectionContentProps> = ({ currentIndex }) => {
  switch (currentIndex) {
    case 0:
      return <LazyMyWeekendPage />;
    case 1:
      return <LazyGuestToolkitPage />;
    case 2:
      return <EventsScheduleSection />;
    case 3:
      return <LazyMarketplacePage />;
    case 4:
      return <LazyAnnouncementsPage />;
    case 5:
      return <LazyChaptersPage />;
    case 6:
      return <LazyKrioFunFactsPage />;
    case 7:
      return <LazyKnowledgePage />;
    case 8:
      return <LazyStoryBuilderMyStoryPage />;
    case 9:
      return <LazySettingsPage />;
    default:
      return null;
  }
};

export default SectionContent;
